import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ZoomIn, ZoomOut, Move } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";

export const CROP_OUTPUT_PX = 1024;
const MIN_ZOOM = 1, MAX_ZOOM = 3;

// Decode with EXIF orientation applied; falls back to <img> (modern browsers orient it too).
async function decodeImage(blob) {
  try {
    return await createImageBitmap(blob, { imageOrientation: "from-image" });
  } catch {
    return await new Promise((resolve, reject) => {
      const url = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("decode")); };
      img.src = url;
    });
  }
}

const dims = (img) => ({ w: img.naturalWidth || img.width, h: img.naturalHeight || img.height });

// Frame geometry: shorter side fills the square at 1×; offset is the image centre relative to the frame centre.
function clampOffset(off, frame, imgW, imgH, zoom) {
  const s = (frame / Math.min(imgW, imgH)) * zoom;
  const maxX = Math.max(0, (imgW * s - frame) / 2), maxY = Math.max(0, (imgH * s - frame) / 2);
  return { x: Math.min(maxX, Math.max(-maxX, off.x)), y: Math.min(maxY, Math.max(-maxY, off.y)) };
}

export async function exportCrop(img, frame, offset, zoom, outPx = CROP_OUTPUT_PX) {
  const { w, h } = dims(img);
  const s = (frame / Math.min(w, h)) * zoom;
  const sw = frame / s;
  const sx = w / 2 - offset.x / s - sw / 2, sy = h / 2 - offset.y / s - sw / 2;
  const size = Math.max(256, Math.min(outPx, Math.round(sw)));  // never upscale tiny sources
  const canvas = document.createElement("canvas");
  canvas.width = size; canvas.height = size;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = "high";
  ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, size, size);  // PNG transparency → white, not black
  ctx.drawImage(img, sx, sy, sw, sw, 0, 0, size, size);
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
  if (!blob) throw new Error("export");
  return blob;
}

function Preview({ url, frame, imgW, imgH, offset, zoom, size, round }) {
  const r = size / frame;
  const s = (frame / Math.min(imgW, imgH)) * zoom * r;
  return (
    <div className={`overflow-hidden bg-slate-100 border border-slate-200 ${round ? "rounded-full" : "rounded-2xl"}`}
         style={{ width: size, height: size }} data-testid={round ? "crop-preview-round" : "crop-preview-square"}>
      <img src={url} alt="" draggable={false} className="max-w-none select-none"
           style={{ width: imgW * s, height: imgH * s,
                    transform: `translate(${size / 2 - imgW * s / 2 + offset.x * r}px, ${size / 2 - imgH * s / 2 + offset.y * r}px)` }} />
    </div>
  );
}

/**
 * Square crop + reframe dialog. `source` is a File/Blob (new upload or the stored original);
 * `onUse(blob)` receives the exported JPEG and may throw to keep the dialog open (e.g. upload failed).
 */
export default function PhotoCropDialog({ source, mode = "new", onCancel, onUse, onDecodeError }) {
  const [img, setImg] = useState(null);
  const [url, setUrl] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [frame, setFrame] = useState(288);
  const [busy, setBusy] = useState(false);
  const frameRef = useRef(null);
  const drag = useRef(null);
  const pointers = useRef(new Map());   // pinch-to-zoom on touch screens
  const pinch = useRef(null);
  const bitmap = useRef(null);

  useEffect(() => {
    if (!source) { setImg(null); setUrl(null); return undefined; }
    let alive = true;
    const u = URL.createObjectURL(source);
    setUrl(u); setZoom(1); setOffset({ x: 0, y: 0 }); setImg(null);
    decodeImage(source).then((d) => { if (alive) { bitmap.current = d; setImg(d); } else d.close?.(); })
      .catch(() => { if (alive) onDecodeError?.(); });
    return () => { alive = false; URL.revokeObjectURL(u); bitmap.current?.close?.(); bitmap.current = null; };
  }, [source]); // eslint-disable-line react-hooks/exhaustive-deps

  // Frame fits phones: measure the dialog's available width.
  const measure = useCallback((el) => { frameRef.current = el; if (el) setFrame(Math.min(320, el.clientWidth)); }, []);

  const { w, h } = img ? dims(img) : { w: 1, h: 1 };
  const clamped = useMemo(() => clampOffset(offset, frame, w, h, zoom), [offset, frame, w, h, zoom]);
  const s = (frame / Math.min(w, h)) * zoom;

  const setZoomClamped = (z) => setZoom(Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z)));
  const pinchDistance = () => { const [a, b] = [...pointers.current.values()]; return Math.hypot(a.x - b.x, a.y - b.y); };
  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) { drag.current = null; pinch.current = { d: pinchDistance(), zoom }; }
    else drag.current = { x: e.clientX - clamped.x, y: e.clientY - clamped.y };
  };
  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pinch.current && pointers.current.size === 2) setZoomClamped(pinch.current.zoom * (pinchDistance() / pinch.current.d));
    else if (drag.current) setOffset({ x: e.clientX - drag.current.x, y: e.clientY - drag.current.y });
  };
  const onPointerUp = (e) => { pointers.current.delete(e.pointerId); drag.current = null; pinch.current = null; };
  const onWheel = (e) => setZoomClamped(zoom - e.deltaY * 0.002);

  const use = async () => {
    if (!img || busy) return;
    setBusy(true);
    try { await onUse(await exportCrop(img, frame, clamped, zoom)); }
    finally { setBusy(false); }
  };

  const reframe = mode === "reframe";
  return (
    <Dialog open={!!source} onOpenChange={(o) => { if (!o && !busy) onCancel(); }}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-md rounded-2xl p-5 sm:p-6" data-testid="photo-crop-dialog">
        <DialogHeader className="text-left">
          <DialogTitle className="text-lg font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>
            {reframe ? "Adjust framing" : "Frame your photo"}
          </DialogTitle>
          <DialogDescription className="text-xs" data-testid="photo-crop-hint">
            {reframe ? "Reframing your current photo — nothing changes until you click Use photo. " : ""}
            Drag to centre your face. Pinch or use the slider to zoom.
          </DialogDescription>
        </DialogHeader>

        <div ref={measure} className="w-full">
          <div className="mx-auto relative overflow-hidden rounded-2xl bg-slate-900 touch-none select-none cursor-grab active:cursor-grabbing"
               style={{ width: frame, height: frame }} data-testid="photo-crop-frame"
               onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp} onWheel={onWheel}>
            {url && img ? (
              <img src={url} alt="" draggable={false} className="max-w-none pointer-events-none"
                   style={{ width: w * s, height: h * s,
                            transform: `translate(${frame / 2 - w * s / 2 + clamped.x}px, ${frame / 2 - h * s / 2 + clamped.y}px)` }} />
            ) : (
              <div className="h-full w-full grid place-items-center text-xs text-slate-400" data-testid="photo-crop-loading">Opening photo…</div>
            )}
            <div className="pointer-events-none absolute inset-0 rounded-full border-2 border-white/80 shadow-[0_0_0_9999px_rgba(15,23,42,0.45)]" />
            <div className="pointer-events-none absolute bottom-2 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full bg-black/55 px-2 py-0.5 text-[10px] font-semibold text-white">
              <Move size={10} /> Drag to centre your face
            </div>
          </div>

          <div className="mt-4 flex items-center gap-3">
            <ZoomOut size={15} className="text-slate-400 shrink-0" />
            <Slider value={[zoom]} min={MIN_ZOOM} max={MAX_ZOOM} step={0.01} onValueChange={([z]) => setZoom(z)}
                    disabled={!img} aria-label="Zoom" data-testid="photo-crop-zoom" className="flex-1" />
            <ZoomIn size={15} className="text-slate-400 shrink-0" />
            <span className="w-10 text-right text-[11px] font-bold text-slate-500 tabular-nums" data-testid="photo-crop-zoom-value">{zoom.toFixed(1)}×</span>
          </div>

          {img && url && (
            <div className="mt-4 flex items-center gap-4">
              <Preview url={url} frame={frame} imgW={w} imgH={h} offset={clamped} zoom={zoom} size={56} round />
              <Preview url={url} frame={frame} imgW={w} imgH={h} offset={clamped} zoom={zoom} size={56} />
              <p className="text-[11px] text-slate-500">How it appears on lawyer cards and your public profile.</p>
            </div>
          )}
        </div>

        <DialogFooter className="mt-2 flex-row justify-end gap-2">
          <button type="button" onClick={onCancel} disabled={busy} data-testid="photo-crop-cancel-btn"
                  className="h-10 px-4 rounded-xl border border-slate-200 text-xs font-bold text-slate-600 hover:bg-slate-50 disabled:opacity-50">
            Cancel
          </button>
          <Button type="button" onClick={use} disabled={!img || busy} data-testid="photo-crop-use-btn"
                  className="rounded-xl adv-btn-primary font-bold h-10 text-xs">
            {busy ? "Saving…" : "Use photo"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
